import React from "react";
import styled, { keyframes, css } from "styled-components";
import theme from "../../UI/theme";

/**
 * AiFlairText
 * A highly-configurable shimmer text pill with optional star sparkles.
 *
 * Props:
 * - text: string | React.ReactNode — the content to render inside the pill (default: "Waved is learning")
 * - showCondition: boolean — controls whether the component renders at all (default: true)
 * - showStars: boolean — toggles the left sparkle cluster (default: true)
 * - showBorder: boolean — toggles the pill border (default: true)
 * - width: string — CSS width for the outer container (default: "100%")
 * - className?: string — passthrough for styled-components overrides
 * - style?: React.CSSProperties — inline style passthrough
 */

const Container = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => p.$width || "100%"};
  height: ${(p) => p.$height || "fit-content"};

  &:hover {
    cursor: ${(p) => (p.onClick ? "pointer" : "default")};
  }
`;

const Pill = styled.div`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: ${(p) => (p.$showStars ? "30px 1fr" : "1fr")};
  gap: 8px;
  align-items: center;
  height: ${(p) => p.$height || "40px"};
  padding: ${(p) => p.$padding || "0px 16px 2px 16px"};
  border-radius: calc((${(p) => p.$height || "40px"} + 20px) / 2);

  ${(p) =>
    p.$showBorder
      ? css`
          border: ${(p) => p.$borderWidth || "2px"} solid ${theme.colors.greenEnergy};

          /* Only the border gets 60% strength where supported */
          @supports (color: color-mix(in srgb, red, transparent)) {
            border-color: color-mix(
              in srgb,
              ${theme.colors.greenEnergy} 60%,
              transparent
            );
          }
        `
      : css`
          border: none;
        `}

`;

const EmojiWrap = styled.div`
  position: relative;
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
`;

/* Glow + fade + scale + slight rotate, color-cycling between the two theme colors */
const sparkle = keyframes`
  0%   { opacity: 0;   transform: scale(0.6) rotate(0deg);   color: ${theme.colors.crystalBlue}; text-shadow: 0 0 6px currentColor; }
  20%  { opacity: 1;   transform: scale(1.0) rotate(5deg);  color: ${theme.colors.crystalBlue}; text-shadow: 0 0 12px currentColor; }
  50%  { opacity: 1;   transform: scale(1.05) rotate(-5deg); color: ${theme.colors.greenEnergy}; text-shadow: 0 0 16px currentColor; }
  80%  { opacity: 0.85; transform: scale(0.9) rotate(0deg);  color: ${theme.colors.crystalBlue}; text-shadow: 0 0 10px currentColor; }
  100% { opacity: 0;   transform: scale(0.6) rotate(0deg);   color: ${theme.colors.crystalBlue}; text-shadow: 0 0 6px currentColor; }
`;

/* A single sparkle (a small ✦ character) placed around the left area) */
const Star = styled.span`
  position: absolute;
  left: ${(p) => p.$left || 0}px;
  top: ${(p) => p.$top || 0}px;
  font-size: ${(p) => p.$size || 10}px;
  line-height: 1;
  pointer-events: none;
  opacity: 0;

  animation: ${sparkle} ${(p) => p.$duration || 2200}ms ease-in-out infinite;
  animation-delay: ${(p) => p.$delay || 0}ms;
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const opacityShimmer = keyframes`
  0%   { -webkit-mask-position: -200% 0; mask-position: -200% 0; }
  100% { -webkit-mask-position:  200% 0; mask-position:  200% 0; }
`;

const TextWrap = styled.div`
  position: relative;
  display: inline-block;
`;

const textShimmerCss = css`
  background: linear-gradient(
    90deg,
    ${theme.colors.crystalBlue} 0%,
    ${theme.colors.greenEnergy} 30%,
    ${theme.colors.crystalBlue} 70%,
    ${theme.colors.greenEnergy} 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 21s linear infinite;
`;

/* Base: constant opacity */
const TextBase = styled.div`
  ${textShimmerCss}
    opacity: 1;
    font-size: ${(p) => p.$fontSize || '13px'};

    min-width: 0;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2; /* max 2 lines */
    overflow: hidden;

    @media (max-width: ${theme.screenSizes.small}px) {
      font-size: ${(p) => p.$mobileFontSize || p.$fontSize || "13px"};
    }
`;

/* Dim layers: masked highlights sweeping across */
const TextDim = styled.div`
  ${textShimmerCss}
  position: absolute;
  inset: 0;
  pointer-events: none;
  font-size: ${(p) => p.$fontSize || '13px'};
  filter: brightness(0.5);
  will-change: filter, -webkit-mask-position, mask-position;

  /* Soft edges (no hard line) */
  -webkit-mask-image: linear-gradient(
    90deg,
    rgba(0,0,0,0) 0%,
    rgba(0,0,0,0.12) 30%,
    rgba(0,0,0,0.35) 42%,
    rgba(0,0,0,0.75) 48%,
    rgba(0,0,0,1.0) 50%,
    rgba(0,0,0,0.75) 52%,
    rgba(0,0,0,0.35) 58%,
    rgba(0,0,0,0.12) 70%,
    rgba(0,0,0,0) 100%
  );
  -webkit-mask-size: 200% 100%;
  -webkit-mask-repeat: no-repeat;
  mask-image: linear-gradient(
    90deg,
    rgba(0,0,0,0) 0%,
    rgba(0,0,0,0.12) 30%,
    rgba(0,0,0,0.35) 42%,
    rgba(0,0,0,0.75) 48%,
    rgba(0,0,0,1.0) 50%,
    rgba(0,0,0,0.75) 52%,
    rgba(0,0,0,0.35) 58%,
    rgba(0,0,0,0.12) 70%,
    rgba(0,0,0,0) 100%
  );
  mask-size: 200% 100%;
  mask-repeat: no-repeat;

  animation:
    ${shimmer} 21s linear infinite,
    ${opacityShimmer} 16.2s linear infinite;


  @media (max-width: ${theme.screenSizes.small}px) {
    font-size: ${(p) => p.$mobileFontSize || p.$fontSize || "13px"};
  }
`;

const AiFlairText = ({
  simpleText = false,
  text = "Waved is learning",
  showCondition = true,
  showStars = true,
  showBorder = true,
  borderWidth = "2px",
  padding = "0px 16px 2px 16px",
  width = "100%",
  height = "fit-content",
  fontSize = false,
  mobileFontSize = false,
  className,
  style,
  onClick,
}) => {
  if (!showCondition) return null;

  if (simpleText) {
    showStars = false;
    showBorder = false;
    width = "fit-content";
  }

  return (
    <Container $width={width} $height={height} className={className} style={style} onClick={onClick}>
      <Pill $height={height} $showStars={showStars} $showBorder={showBorder} $borderWidth={borderWidth} $padding={padding}>
        {showStars && (
          <EmojiWrap aria-hidden>
            {/* Tiny stars around the left area; each with its own timing */}
            <Star $left={5} $top={8} $size={7} $delay={700} $duration={20000}>
              ✦
            </Star>
            <Star $left={8} $top={19} $size={9} $delay={700} $duration={12000}>
              ✦
            </Star>
            <Star $left={13} $top={8} $size={14} $delay={700} $duration={16000}>
              ✦
            </Star>
          </EmojiWrap>
        )}

        <TextWrap>
          <TextBase $fontSize={fontSize} $mobileFontSize={mobileFontSize}>{text}</TextBase>

          {/* Multiple masked highlight passes for richer shimmer */}
          <TextDim
                $fontSize={fontSize}
                $mobileFontSize={mobileFontSize}
                aria-hidden
                style={{ animationDirection: "normal" }}
          >
            {text}
          </TextDim>
          <TextDim
            $fontSize={fontSize}
            $mobileFontSize={mobileFontSize}
            aria-hidden
            style={{ animationDirection: "reverse", animationDelay: "3.6s", animationDuration: "12.9s" }}
          >
            {text}
          </TextDim>
          <TextDim
            $fontSize={fontSize}
            $mobileFontSize={mobileFontSize}
            aria-hidden
            style={{ animationDirection: "normal", animationDelay: "5.2s", animationDuration: "21s" }}
          >
            {text}
          </TextDim>
          <TextDim
            $fontSize={fontSize}
            $mobileFontSize={mobileFontSize}
            aria-hidden
            style={{ animationDirection: "reverse", animationDelay: "1.8s", animationDuration: "16.2s" }}
          >
            {text}
          </TextDim>
        </TextWrap>
      </Pill>
    </Container>
  );
};

export default AiFlairText;
